import React, { Component, useState } from "react";
import { EuiBasicTable, EuiLink, EuiForm, EuiFlexItem, EuiButton, EuiSwitch, EuiSpacer } from '@elastic/eui';
import { BlockReason } from "./BlockReason";
import { EuiBasicTableColumn } from "@elastic/eui/src/components/basic_table/basic_table";
import {
    authentication,
    fetchServerInfo, isDevMode,
    updateBMServer,
    updateServerApi
} from "../application/FirebaseConfigs";
import { IpList } from "./IpList";
import { BMServer, BMServerToUpdate } from "../model/BMServer";
import { BMServerTraffic } from "../model/BMServerTraffic";

interface IProps {
    servers: Map<String, BMServer>;
    setServers: React.Dispatch<React.SetStateAction<Map<String, BMServer>>>
    onUpdated: (status: boolean, res: any, content: string) => void
    traffic: BMServerTraffic
}

export class UpdateIPForm extends Component<IProps, any> {
    servers: BMServerToUpdate[] = [];
    constructor({ props }: { props: any }) {
        super(props);
        this.state = {
            modalDisplay: false,
            sortDirection: 'asc',
            serverData: [],
            buttonText: 'Update',
            isTestMode: false,
            /** When true, cloud function also writes public S3 (data.json / conf.json). */
            syncToS3: true
        };
    }
    componentDidMount() {
        this.updateServer(this.props.servers)
        this.setState({ serverData: this.restructureData(this.servers) })
    }

    restructureData(serverData: BMServerToUpdate[]) {
        let sorted = serverData.sort((a, b) => b.traffic - a.traffic)
        let serverToCompare: BMServerToUpdate | undefined;
        sorted.forEach((n, i) => {
            if (serverToCompare) {
                n.hasBadTraffic = n.traffic < serverToCompare.traffic
            }
            serverToCompare = n;
            sorted[i] = n;
        })
        return sorted.reverse();
    }

    test(row: BMServerToUpdate) {
        return row.ips;
    }
    updateServer(serversObj: Map<String, BMServer>) {
        this.servers = []
        console.log("updateServer")
        console.log(this.props.traffic)
        for (let server of Array.from(serversObj.values())) {
            const activeIp = server.ips.find(i => i.ip === server.currentIp);
            this.servers.push({
                updatedBy: server.updatedBy,
                updatedOn: server.updatedOn,
                currentIp: server.currentIp, ips: server.ips, link: server.link, newIp: server.currentIp, reason: "", serverName: server.serverName, traffic: server.traffic, hasBadTraffic: false, serverType: server.serverType,
                app: server.app,
                isPremium: server.isPremium,
                manualRegion: server.manualRegion,
                dualHttpsEnabled: !!activeIp?.dualHttpsEnabled,
                dualHttpsOnlyPairs: (server.dualHttpsOnlyPairs || []).map(p => ({ ...p }))
            })
        }
    }

    private getTraffic(currentServer: BMServer) {
        console.log(currentServer)
        let foundTraffic = this.props.traffic.servers.get(currentServer.currentIp)
        if (foundTraffic) {
            return Number(foundTraffic).toFixed(0)
        }
        return 0;
    }

    private getSelectedIpInfo(row: BMServerToUpdate) {
        // Always resolve against the New IP selection (not currentIp),
        // otherwise find() can match the old primary first and show the wrong secondary.
        const selected = (row.newIp || row.currentIp || "").trim().replace(":443", "");
        if (!selected) return undefined;
        return row.ips.find(i => {
            const ip = (i.ip || "").trim().replace(":443", "");
            return ip === selected || i.ip === row.newIp;
        });
    }

    private getSecondaryIp(row: BMServerToUpdate): string {
        return (this.getSelectedIpInfo(row)?.secondaryIp || "").trim();
    }

    private validateDualHttps(): string | null {
        for (const row of this.state.serverData as BMServerToUpdate[]) {
            if (row.dualHttpsEnabled && !this.getSecondaryIp(row)) {
                return `${row.serverName}: Dual HTTPS is on but selected IP (${row.newIp || row.currentIp}) has no secondary IP. Set secondary in server config first.`;
            }
            for (const pair of row.dualHttpsOnlyPairs || []) {
                if (!pair.enabled) continue;
                if (!(pair.primaryIp || "").trim() || !(pair.secondaryIp || "").trim()) {
                    return `${row.serverName}: dual-HTTPS-only pair is enabled but primary/secondary is empty.`;
                }
            }
        }
        return null;
    }

    render() {
        const columns: Array<EuiBasicTableColumn<BMServerToUpdate>> = [
            {
                field: 'serverName',
                name: 'Server',
                render: (v, r) => (<span>{v}- <small style={r.hasBadTraffic ? { color: "#F00" } : { color: "#0F0" }} > <b>( {this.getTraffic(r)} MBps)</b></small></span>)
            },
            {
                field: 'app',
                name: 'App',
                width: '80px',
                render: (v: any) => <span>{v || "SPL"}</span>
            },
            {
                field: 'serverType', // Added Server Type Column
                name: 'Type',
                width: '100px',
                render: (v: any) => <span>{v || "general"}</span>
            },
            {
                field: 'isPremium',
                name: 'Premium',
                width: '80px',
                render: (v, r: BMServerToUpdate) => (
                    <EuiSwitch
                        showLabel={false}
                        label="Premium"
                        checked={r.isPremium || false}
                        onChange={(e) => {
                            r.isPremium = e.target.checked;
                            this.setState({ serverData: [...this.state.serverData] });
                        }}
                    />
                )
            },
            {
                field: 'dualHttpsEnabled',
                name: 'Dual HTTPS',
                width: '220px',
                render: (v, r: BMServerToUpdate) => {
                    const secondary = this.getSecondaryIp(r);
                    const onlyPairs = r.dualHttpsOnlyPairs || [];
                    return (
                        <div>
                            <EuiSwitch
                                showLabel={false}
                                label="Dual HTTPS"
                                checked={!!r.dualHttpsEnabled}
                                onChange={(e) => {
                                    if (e.target.checked && !secondary) {
                                        alert(`${r.serverName}: set a secondary IP for ${r.newIp || r.currentIp} in server config before enabling Dual HTTPS.`);
                                        return;
                                    }
                                    r.dualHttpsEnabled = e.target.checked;
                                    this.setState({ serverData: [...this.state.serverData] });
                                }}
                            />
                            {r.dualHttpsEnabled ? (
                                <div style={{ fontSize: 11, marginTop: 4, color: secondary ? "#0077CC" : "#BD271E" }}>
                                    {secondary
                                        ? `sec for ${(r.newIp || r.currentIp || "").replace(":443", "")}: ${secondary}`
                                        : `no secondary for ${(r.newIp || r.currentIp || "").replace(":443", "")}`}
                                </div>
                            ) : null}
                            {onlyPairs.length > 0 ? (
                                <div style={{ fontSize: 11, marginTop: 6 }}>
                                    {onlyPairs.map(pair => (
                                        <label key={pair.id} style={{ display: "block", marginTop: 2 }}>
                                            <input
                                                type="checkbox"
                                                checked={!!pair.enabled}
                                                onChange={(e) => {
                                                    pair.enabled = e.target.checked;
                                                    this.setState({ serverData: [...this.state.serverData] });
                                                }}
                                            />
                                            {" "}only: {(pair.primaryIp || "?").slice(0, 18)}→{(pair.secondaryIp || "?").slice(0, 18)}
                                        </label>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                }
            },
            {
                field: 'manualRegion',
                name: 'Region',
                width: '100px',
                render: (v, r: BMServerToUpdate) => (
                    <input
                        type="text"
                        style={{ width: "100%", padding: "4px", borderRadius: "4px", border: "1px solid #ccc" }}
                        placeholder="AUTO"
                        value={r.manualRegion || ""}
                        onChange={(e) => {
                            r.manualRegion = e.target.value;
                            this.setState({ serverData: [...this.state.serverData] });
                        }}
                    />
                )
            },
            {
                field: 'currentIp',
                name: 'IP',
            },
            {
                field: 'newIp',
                name: 'New IP',
                render: (v, r: BMServerToUpdate) => (<IpList onChange={(selectedIp) => {
                    r.newIp = selectedIp;
                    const ipInfo = r.ips.find(i => i.ip === selectedIp);
                    r.dualHttpsEnabled = !!ipInfo?.dualHttpsEnabled;
                    // Keep enabled only if selected IP still has secondary
                    if (r.dualHttpsEnabled && !(ipInfo?.secondaryIp || "").trim()) {
                        r.dualHttpsEnabled = false;
                    }
                    this.setState({ serverData: [...this.state.serverData] });
                }} rowData={r} />)
            },
            {
                field: 'reason',
                name: 'Reason',
                render: (v, r: BMServerToUpdate) => (<BlockReason onChange={(newReason) => {
                    r.reason = newReason;
                    this.setState({ serverData: [...this.state.serverData] });
                }} />)
            }
        ]

        return <EuiForm >
            <EuiBasicTable
                tableCaption="Demo of EuiBasicTable"
                items={this.state.serverData}
                rowHeader="serverName"
                columns={columns}
                className={"custom-table-x"}
            />
            <EuiSpacer size="m" />
            <EuiSwitch
                label="Test Mode (Test config keys)"
                checked={this.state.isTestMode}
                onChange={(e) => this.setState({ isTestMode: e.target.checked })}
            />
            <EuiSpacer size="s" />
            <EuiSwitch
                label="Sync to S3 (public data.json) — off = Firebase RC only"
                checked={this.state.syncToS3}
                onChange={(e) => this.setState({ syncToS3: e.target.checked })}
            />
            <div style={{ margin: "20px 0 0 0 " }}>
                <EuiFlexItem grow={false}>
                    <EuiButton
                        disabled={this.state.buttonText != "Update"}
                        color="primary"
                        size="s"
                        fill
                        onClick={() => { this.formSubmit() }}
                    >
                        {this.state.buttonText}
                    </EuiButton>
                </EuiFlexItem>
            </div>
        </EuiForm>;
    }
    formSubmit() {
        const validationError = this.validateDualHttps();
        if (validationError) {
            alert(validationError);
            return;
        }
        this.setState({ buttonText: "Updating..." })
        this.updateData((r, content) => {
            this.setState({ buttonText: "Update" })
            this.props.onUpdated(true, r, content)
        }, () => {
            this.props.onUpdated(false, "", "RC Failed to update");
        })
    }

    updateData(onSuccess: (res: any, contents: any) => void, onFail: () => void) {
        console.log(this.servers)
        // @ts-ignore
        updateBMServer(this.servers).then((newIpString: { newIp: string, gamingNewIp: string, streamingNewIp: string }) => {
            console.log(newIpString)
            fetchServerInfo((updatedServers) => {
                console.log(updatedServers)
                console.log(authentication.currentUser?.email)
                this.props.setServers(updatedServers)
                this.updateServer(updatedServers)
                this.setState({ serverData: this.servers })
                this.setState({ serverData: this.servers })

                updateServerApi({
                    newIp: newIpString.newIp, // Access properties from object
                    gamingNewIp: newIpString.gamingNewIp,
                    streamingNewIp: newIpString.streamingNewIp,
                    isTest: this.state.isTestMode, // Pass test mode flag
                    syncToS3: this.state.syncToS3,
                    "user": authentication.currentUser?.email
                }).then((r) => {
                    console.log(r)
                    // @ts-ignore
                    onSuccess(updatedServers, r.data.config)
                }).catch((err) => {
                    console.log(err);
                    onFail()
                });

            }, () => { })
        }).catch((e) => {
            onFail()
        })
    }
}
